import * as React from "react"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"
import { cn } from "../../ui/lib/utils"

// ScrollArea with always-visible scrollbar
export const VisibleScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root>
>(({ className, children, ...props }, ref) => (
  <ScrollAreaPrimitive.Root
    ref={ref}
    className={cn("relative overflow-hidden", className)}
    {...props}
  >
    <ScrollAreaPrimitive.Viewport className="relative h-full w-full rounded-[inherit]">
      {children}
    </ScrollAreaPrimitive.Viewport>
    <VisibleScrollBar />
    <ScrollAreaPrimitive.Corner />
  </ScrollAreaPrimitive.Root>
))
VisibleScrollArea.displayName = 'VisibleScrollArea';

// Custom scrollbar that's always visible
const VisibleScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none",
      orientation === "vertical" &&
        "h-full w-4 border-l border-l-transparent p-[2px]", // Wider scrollbar
      orientation === "horizontal" &&
        "h-4 flex-col border-t border-t-transparent p-[2px]", // Taller horizontal scrollbar
      "always-visible", // Custom class
      className
    )}
    style={{ opacity: 1 }}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb 
      className={cn(
        "relative flex-1 rounded-full",
        "bg-[#4a5567]", // Darker, more visible color
        "hover:bg-[#5a6577]", // Even darker on hover
        "transition-colors duration-150"
      )} 
    />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
VisibleScrollBar.displayName = 'VisibleScrollBar';

// Add CSS to make the scrollbar always visible
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = `
    .always-visible {
      opacity: 1 !important;
      transition: none !important;
    }
    .always-visible:hover {
      opacity: 1 !important;
    }
    
    /* Ensure the thumb (actual scrollbar) has a minimum size */
    [data-radix-scroll-area-thumb] {
      min-height: 40px;
    }
  `;
  document.head.appendChild(style);
} 